import * as React from 'react';
import { styled } from 'styled-components';
import { sessionKeys } from '../../../../core/localStorage';
import { documentTimeStampFormat, documentViewPageUrl, partnerUserType } from '../../../../core/constants';
import { useNavigateWithAppName } from '../../../common/NavigateWithAppName';
import {
	greyColour,
	superLightGreyColour,
	labelGreyColour,
	labelBlueColour,
	labelOrangeColour,
	labelRedColour,
	tabletPortraitMinWidth,
	okGreen2
} from '../../../cssConstants';
import { DocumentVaultModel } from '../../../../models/DocumentVaultModel';
import { formatDateWithTime } from '../../../../lib/utils';
import ViewAllButton from '../../common/ViewAllButton';
import { DocumentExchangeItems } from '../../../../core/enums';
import ArrowRightGradientSvg from '../../../../images/arrowRightGradient.svg';
import { ButtonIcon, CardButton, OldCardContainer } from '../../common/CardLayout';

const DocumentVaultListItemSection = ({ document, documentTabItem }: Props) => {
	const navigate = useNavigateWithAppName();
	const hasNotification = documentTabItem === DocumentExchangeItems.Received;

	const handleDocumentClick = (documentVaultId: number) => {
		navigate(`${documentViewPageUrl}?${sessionKeys.documentVaultId}=${documentVaultId}`);
	};

	return (
		<OldCardContainer>
			<DocumentListItem>
				{hasNotification && (!document.isRead || !document.isCommentRead) && <NotificationCircle />}
				<DocumentInfo>
					<DocumentItemTitle>{document.title}</DocumentItemTitle>
					<DocumentDescription $color={superLightGreyColour}>{document.fileName}</DocumentDescription>
					<DocumentDescription $color={getPriorityColor(document.priorityLevel)}>
						{/* Uses '&nbsp;' (non-breaking space) to maintain the element's height and preserve layout spacing */}
						{document.userType === partnerUserType ? document.priorityLevel : <>&nbsp;</>}
					</DocumentDescription>
					<DocumentTimeStamp $color={labelGreyColour}>
						{formatDateWithTime(document.dateCreated, documentTimeStampFormat)}
					</DocumentTimeStamp>
				</DocumentInfo>
				<CardButton onClick={() => handleDocumentClick(document.documentVaultId)}>
					<ButtonIcon src={ArrowRightGradientSvg} alt="forward icon" />
				</CardButton>
			</DocumentListItem>
		</OldCardContainer>
	);
};

export default DocumentVaultListItemSection;

export const getPriorityColor = (priorityLevel: string) => {
	switch (priorityLevel) {
		case 'High Priority':
			return labelRedColour;
		case 'Medium Priority':
			return labelOrangeColour;
		case 'Low Priority':
			return labelBlueColour;
		default:
			return labelGreyColour;
	}
};

interface Props {
	document: DocumentVaultModel;
	documentTabItem: DocumentExchangeItems;
}

export const DocumentInfo = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	height: 100%;
	padding-top: 8px;
`;

const DocumentListItem = styled.li`
	display: flex;
	flex-direction: column;
	padding: 16px;
	width: 100%;
`;

export const DocumentItemHeader = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	margin-bottom: 3px;
`;

export const DocumentItemTitle = styled.p<{ $hasNotification?: boolean }>`
	color: ${greyColour};
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0.1px;
	overflow: hidden;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-bottom: 12px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 16px;
		line-height: 24px;
	}
`;

export const DocumentDescription = styled.p<{ $color?: string }>`
	color: ${({ $color }) => $color};
	font-size: 12px;
	font-weight: 400;
	line-height: 16px;
	letter-spacing: 0.4px;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	margin-bottom: 3px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 14px;
		line-height: 20px;
	}
`;

const DocumentTimeStamp = styled(DocumentDescription)`
	color: ${labelGreyColour};
	padding-top: 5px;
	margin-bottom: 0;
`;

export const NotificationCircle = styled.div`
	position: absolute;
	top: 12px;
	right: 12px;
	width: 9px;
	height: 9px;
	border-radius: 50%;
	background: linear-gradient(175deg, #006cb8, ${okGreen2});
`;

export const StyledViewButton = styled(ViewAllButton)`
	color: ${greyColour};
	margin-bottom: 10px;
	margin-left: auto;
	padding: 0;
`;
